import { useHydrateAtoms }               from "jotai/utils";
import { Fragment, useEffect, useState } from "react";

import { storeCTEvent }                           from "_analytics/index";
import { useSetTheatreMode }                      from "_common/hooks/global";
import { broadcastIdAtom, useSetBroadcastId }     from "_common/hooks/stream";
import useBeforeUnload                            from "_common/hooks/useBeforeUnload";
import { StreamPageTags }                         from "_common/seo";
import { initialValidations }                     from "_common/ssrWrapper";
import { enableBackupAPIsInCookies, getOptImage } from "_common/utils";
import { formatLiveSession }                      from "_common/utils/broadcast";
import ControlHeight                              from "_components/atoms/controlHeight";
import {
	dynamicData,
	isModerator,
	pinnedComment,
	useSetDynamicData,
	useSetIsModerator,
	useSetPinnedComment
} from "_components/features/ivsChat/states";
import { remainingStreamTimeAtom, useSetRemainingStreamTime } from "_components/features/player/states";
import Body                                                   from "_components/pages/stream";
import { sessionData, useSetUpdateSession }                   from "_jotai/liveSession";
import { getPreviousCommentsInLiveStream }                    from "_services/api/broadcast";
import { getBroadcastSession }                                from "_services/api/broadcast";
import { getRecommendedSessions, getSEOTags }                 from "_services/api/feed";

const component = props => {
	const [ backupBroadcastData, setBackupBroadcastData ] = useState ( {} );
	const [ backupSessions, setBackupSessions ]           = useState ( [] );
	const [ _sessions, setSessions ]                      = useState ( [] );

	const broadcastId = props?.broadcastId;
	const seoTags     = props?.seoTags;

	const broadcastData = Object.keys ( backupBroadcastData || {} ).length ? backupBroadcastData : ( props?.broadcastData || {} );

	let sessions = backupSessions?.length ? backupSessions : ( _sessions || [] );

	let data = sessions?.filter ( session => session?.id !== parseInt ( broadcastId ) );

	data = data?.splice ( 0, 12 );

	sessions = data.map ( session => {
		return formatLiveSession ( session );
	} );

	const setRemainingStreamTime = useSetRemainingStreamTime ();
	const setToggleTheatreMode   = useSetTheatreMode ();
	const setBroadcastId         = useSetBroadcastId ();
	const setPinnedComment       = useSetPinnedComment ();
	const setDynamicData         = useSetDynamicData ();
	const setIsModerator         = useSetIsModerator ();
	const setSessionData         = useSetUpdateSession ();

	useHydrateAtoms ( [
		[ broadcastIdAtom, broadcastId ],
		[ remainingStreamTimeAtom, props?.broadcastData?.remainingWatchDuration ],
		[ sessionData, broadcastData ],
		[ pinnedComment, broadcastData?.pinnedCommentDetails ],
		[ isModerator, broadcastData?.isCurrentUserModerator ],
		[ dynamicData, { liveViews: broadcastData?.liveViews } ]
	] );

	const sendCTEvent = () => {
		const broadcaster = broadcastData?.broadcaster;
		const creatorName = broadcaster?.sportsFan?.name;
		const creatorId   = broadcaster?.sportsFan?.id;
		const gameName    = broadcastData?.game?.name;

		storeCTEvent ( "live_broadcast_interacted_new", {
			br_name          : creatorName,
			br_sports_fan_id : creatorId,
			game_name        : gameName,
			auto_play        : false,
			device           : "web"
		} );
	};

	useBeforeUnload ( sendCTEvent );

	const getBackupData = async () => {
		try {
			const results = await Promise.allSettled (
				[
					getBroadcastSession ( broadcastId, props?.viewerCountry )
				]
			);

			let broadcast, sessions;

			results.forEach ( ( result, index ) => {
				if ( result.status === "fulfilled" ) {
					switch ( index ) {
						case 0:
							broadcast = result.value;
							break;

						case 1:
							sessions = result.value;
							break;

					}
				}
			} );

			setBackupBroadcastData ( broadcast );
			setBackupSessions ( sessions );

		} catch ( error ) {
			// Do nothing
		}
	};

	const fetchSessions = async () => {
		try {
			const results = await getRecommendedSessions ( { pageNo: 1, broadcastId } );

			setSessions ( results );
		} catch ( err ) {
			// do nothing
		}
	};

	useEffect ( () => {
		if ( Object.keys ( props?.broadcastData || {} ).length )
			return;

		enableBackupAPIsInCookies ();
		getBackupData ();
	}, [] );

	useEffect ( () => {
		fetchSessions ();
		setBroadcastId ( broadcastId );
		setSessionData ( broadcastData );
		setPinnedComment ( broadcastData?.pinnedCommentDetails );
		setDynamicData ( { liveViews: broadcastData?.liveViews } );
		setIsModerator ( broadcastData?.isCurrentUserModerator );

		return () => {
			setBroadcastId ( undefined );
			setToggleTheatreMode ( false );

			if ( broadcastData?.endTimeUTC )
				return;

			sendCTEvent ();
		};
	}, [ broadcastId ] );

	const streamTitle = broadcastData?.sessionInfo;
	const userName    = broadcastData?.broadcaster?.sportsFan?.name;
	const thumbnail   = getOptImage ( broadcastData?.thumbnail );
	const isLive      = broadcastData && !broadcastData.endTimeUTC;
	const streamURL   = isLive ? broadcastData.cdnUrl : broadcastData.streamingURL;

	useEffect ( () => {
		setRemainingStreamTime ( props?.broadcastData?.remainingWatchDuration );
	}, [ props?.broadcastData?.remainingWatchDuration ] );

	return (
		<Fragment>
			<StreamPageTags
				description = { broadcastData?.description }
				endpoint = { props?.currentPath }
				seoTags = { seoTags }
				startTimeUTC = { broadcastData?.startTimeUTC }
				streamTitle = { streamTitle }
				streamURL = { streamURL }
				thumbnail = { thumbnail }
				userName = { userName }
			/>

			<Body
				key = { broadcastId }
				broadcastId = { broadcastId }
				isMobile = { props?.isMobile }
				previousComments = { props?.previousComments }
				sessions = { sessions }
			/>

			<ControlHeight />
		</Fragment>
	);
};

const getServerSideProps = async context => initialValidations ( context, async ( defaultProps, context ) => {
	const broadcastId = context.query?.id;

	if ( !broadcastId )
		return { props: { ...defaultProps } };

	let seoTags, pageName = "streamPage";

	const results = await Promise.allSettled (
		[
			getBroadcastSession.bind ( defaultProps ) ( broadcastId, defaultProps?.viewerCountry ),
			getPreviousCommentsInLiveStream.bind ( defaultProps ) ( { broadcastSessionId: broadcastId, timestamp: Date.now (), pageSize: 150 } ),
			getSEOTags.bind ( defaultProps ) ( { pageName } )
		]
	);

	let broadcast, previousComments = [], forceLogout = false;

	results.forEach ( ( result, index ) => {
		if ( result.status === "fulfilled" ) {
			switch ( index ) {
				case 0:
					broadcast = result.value;
					break;

				case 1:
					previousComments = result.value;
					break;

				case 2:
					seoTags = result.value;
					break;

			}
		}

		if ( result.status === "rejected" && result.reason?.message === "TOKEN_EXPIRED_FORCE_LOGOUT" )
			forceLogout = true;
	} );

	if ( forceLogout )
		return {
			redirect: {
				destination : `/logout?forceLogout=${ true }&prevLocation=${ defaultProps?.currentPath }`,
				permanent   : false
			}
		};

	if ( !broadcast || !Object.keys ( broadcast ).length )
		return {
			notFound: true
		};

	if ( !defaultProps?.isLoggedIn )
		context.res.setHeader (
			"Cache-Control",
			"public, s-maxage=90, stale-while-revalidate=120"
		);

	const removeDeletedComments = previousComments.filter ( comment => !comment.deleted );
	const addTypeInComments     = removeDeletedComments.map ( item => ( { ...item, type: "USER_COMMENT", uniqueID: item.id } ) );

	return {
		props: {
			...defaultProps,
			broadcastId,
			broadcastData    : broadcast,
			previousComments : addTypeInComments,
			seoTags          : seoTags?.seo_tags || []
		}
	};

}, { blockOBS: true } );

export { getServerSideProps };

export default component;
